import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Shield, CalendarDays, AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { accountAPI, handleApiError } from '../utils/api';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { updateSession } = useAuth();

  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await accountAPI.getProfile();
        if (cancelled) return;
        setProfile(response.data);
        setUsername(response.data.username || '');
        setEmail(response.data.email || '');
      } catch (err) {
        if (cancelled) return;
        const { error: errTitle, details } = handleApiError(err);
        setError(`${errTitle} : ${details}`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const emailChanged = profile && email.trim().toLowerCase() !== (profile.email || '').toLowerCase();
  const usernameChanged = profile && username.trim() !== (profile.username || '');
  const hasChanges = emailChanged || usernameChanged;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (username.trim().length < 3) {
      setError('Le nom d\'utilisateur doit contenir au moins 3 caractères');
      return;
    }
    if (emailChanged && !currentPassword) {
      setError('Saisissez votre mot de passe actuel pour changer d\'email');
      return;
    }

    setIsSaving(true);
    try {
      const response = await accountAPI.updateProfile({
        username: username.trim(),
        email: email.trim(),
        currentPassword: emailChanged ? currentPassword : undefined,
      });
      const data = response.data;
      updateSession({ token: data.token, username: data.username, email: data.email });
      setProfile((prev) => ({ ...prev, username: data.username, email: data.email }));
      setUsername(data.username);
      setEmail(data.email);
      setCurrentPassword('');
      setSuccess('Profil mis à jour');
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <h1 className="text-2xl font-bold flex items-center">
              <User className="mr-3" size={28} />
              Mon profil
            </h1>
          </div>

          <div className="p-6">
            {isLoading ? (
              <p className="text-gray-500 text-center py-6">Chargement...</p>
            ) : (
              <>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm">
                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm">
                    <CheckCircle2 size={18} className="shrink-0" />
                    <span>{success}</span>
                  </div>
                )}

                {profile && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield size={16} className="text-gray-400" />
                        Rôle : <span className="font-semibold text-gray-800">{profile.role === 'ADMIN' ? 'Administrateur' : 'Utilisateur'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <CalendarDays size={16} className="text-gray-400" />
                        Inscrit le <span className="font-semibold text-gray-800">{formatDate(profile.createdAt)}</span>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className={inputClass}
                          maxLength={50}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <Mail size={14} /> Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={inputClass}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">L'email sert d'identifiant de connexion.</p>
                      </div>
                      {emailChanged && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mot de passe actuel (requis pour changer d'email)
                          </label>
                          <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className={inputClass}
                            autoComplete="current-password"
                          />
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={isSaving || !hasChanges}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </form>

                    <button
                      onClick={() => navigate('/change-password')}
                      className="mt-6 text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <KeyRound size={14} /> Changer mon mot de passe
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
